import { CommonModule } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import ApplicationsAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/authorization/applications/applications.entity";
import { OperationSystemEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/operating-system.enum";
import { ApplicationService } from "../../../../document/applications/applications.service";
import { HandlerMessageService } from "app/common/services/handler-error/handler-message.service";
import {
  OPERATING_SYSTEM_ICONS,
  OPERATING_SYSTEM_LABELS,
  OPERATING_SYSTEM_STORES,
  PLATFORM_CARDS_ORDER,
} from "../operating-system.constants";
import SubApplicationsAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/authorization/sub-applications/sub-applications.entity";
import { CompanySubApplicationService } from "../company-sub-application.service";
import CompanyAppVersionAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/company/company-sub-application/company-app-version/company-app-version.entity";
import { VersionFormModalData } from "../interfaces/company-sub-application.interface";

/** Formato de versión: major.minor.patch (ej: 1.0.0, 3.4.4) */
const VERSION_FORMAT_PATTERN = /^\d+\.\d+\.\d+$/;

@Component({
  selector: "app-version-form-modal",
  templateUrl: "./version-form-modal.component.html",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
  ],
})
export class VersionFormModalComponent implements OnInit {
  form: FormGroup;
  isEditMode: boolean;
  saving = false;
  listApplication: ApplicationsAttributes[] = [];
  listSubAplication: SubApplicationsAttributes[] = [];
  readonly platformOrder = PLATFORM_CARDS_ORDER;
  readonly platformLabels = OPERATING_SYSTEM_LABELS;
  readonly platformStores = OPERATING_SYSTEM_STORES;
  readonly platformIcons = OPERATING_SYSTEM_ICONS;

  private companyId: string;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<VersionFormModalComponent>,
    private applicationService: ApplicationService,
    private companySubAppService: CompanySubApplicationService,
    private handlerMessage: HandlerMessageService,
    @Inject(MAT_DIALOG_DATA) public data: VersionFormModalData
  ) {
    this.companyId = data.companyId;
    const version = data.version ?? null;
    this.isEditMode = !!version;
    const versionsMap = this.buildVersionsMap(version?.appVersions ?? []);
    const versionsGroup = this.fb.group(
      PLATFORM_CARDS_ORDER.reduce(
        (acc, key) => ({
          ...acc,
          [key]: [
            versionsMap[key] ?? "",
            [
              Validators.required,
              Validators.pattern(/\S/),
              Validators.pattern(VERSION_FORMAT_PATTERN),
            ],
          ],
        }),
        {} as Record<OperationSystemEnum, unknown>
      )
    );
    this.form = this.fb.group({
      subApplication: [version?._id ?? null, this.isEditMode ? [] : [Validators.required]],
      code: [version?.code ?? "", [Validators.required]],
      name: [version?.name ?? "", [Validators.required]],
      type: [version?.type ?? "APP", Validators.required],
      versions: versionsGroup,
      statusId: [version?.statusId ?? 1, Validators.required],
    });
  }

  ngOnInit(): void {
    if (!this.isEditMode) {
      this.loadApplications();
      this.form.get("subApplication")?.valueChanges.subscribe((subAppId: string | null) => {
        if (subAppId) {
          const subApp = this.listSubAplication.find((s) => (s as any)?._id === subAppId);
          if (subApp) {
            this.form.patchValue({ code: subApp.code, name: subApp.name }, { emitEvent: false });
          }
        }
      });
    }
  }

  loadApplications(): void {
    this.applicationService.findToComboBox().subscribe({
      next: (res: any) => {
        const apps = res?.data ?? res ?? [];
        this.listApplication = Array.isArray(apps) ? apps : [];
        const dataSubAplicaciones: SubApplicationsAttributes[] = [];
        this.listApplication.forEach((item) => {
          const subs = item.subApplications ?? [];
          dataSubAplicaciones.push(...subs);
        });
        this.listSubAplication = dataSubAplicaciones;
      },
    });
  }

  private buildVersionsMap(appVersions: CompanyAppVersionAttributes[]): Partial<Record<OperationSystemEnum, string>> {
    return appVersions.reduce(
      (acc, av) => ({ ...acc, [av.operatingSystem]: av.version }),
      {} as Partial<Record<OperationSystemEnum, string>>
    );
  }

  get versionsGroup(): FormGroup {
    return this.form.get("versions") as FormGroup;
  }

  save(): void {
    if (this.form.invalid) return;
    const value = this.form.getRawValue();
    const appVersionsPayload = this.platformOrder.map((operatingSystem) => ({
      operatingSystem,
      version: value.versions[operatingSystem].trim(),
    }));

    this.saving = true;
    const version = this.data.version;

    if (this.isEditMode && version?._id) {
      this.companySubAppService
        .updateEmbedded(this.companyId, String(version._id), { appVersions: appVersionsPayload })
        .subscribe({
          next: () => {
            this.saving = false;
            this.handlerMessage.success({ message: "Versiones actualizadas correctamente." });
            this.dialogRef.close({
              _id: version._id,
              code: value.code,
              name: value.name,
              type: value.type,
              appVersions: appVersionsPayload.map((av) => ({ ...av, _id: undefined })),
              statusId: value.statusId,
            });
          },
          error: (err) => {
            this.saving = false;
            this.handlerMessage.error({
              message: err?.error?.message,
              alternativeMessage: "Error al actualizar las versiones.",
              statusCode: err?.status,
            });
          },
        });
    } else {
      const subApplicationId = value.subApplication;
      if (!subApplicationId) {
        this.handlerMessage.error({ message: "Debe seleccionar una sub-aplicación." });
        this.saving = false;
        return;
      }
      this.companySubAppService
        .createEmbedded(this.companyId, { subApplicationId, appVersions: appVersionsPayload })
        .subscribe({
          next: (res: any) => {
            this.saving = false;
            this.handlerMessage.success({ message: "Versiones configuradas correctamente." });
            const created = res?.data ?? {};
            this.dialogRef.close({
              _id: created._id ?? subApplicationId,
              code: value.code,
              name: value.name,
              type: value.type,
              appVersions: appVersionsPayload,
              statusId: value.statusId,
            });
          },
          error: (err) => {
            this.saving = false;
            this.handlerMessage.error({
              message: err?.error?.message,
              alternativeMessage: "Error al configurar las versiones.",
              statusCode: err?.status,
            });
          },
        });
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
